import type { FunctionComponent } from 'react';
import { useEffect, useMemo, useState } from 'react';

import { Analytics } from '@trello/atlassian-analytics';
import { ComponentWrapper } from '@trello/component-wrapper';
import { environment } from '@trello/config';
import { sendErrorEvent } from '@trello/error-reporting';
import { currentLocale, languageParts, usesLanguages } from '@trello/locale';

import { getBoardPickerTemplates } from './getBoardPickerTemplates';
import { localizedTemplatesSelector, TemplatePicker } from './TemplatePicker';
import type { Category, LocalizedTemplatesListHash } from './types';

const DISMISS_MESSAGE_ID = `boards-page-template-picker`;
const DATE_FOR_NEW_ACCOUNT = new Date(2020, 5, 1); // June 1st, 2020

export interface BoardsPageTemplatePickerProps {
  category: Category;
  isNewAccount: (date: Date) => boolean;
  isDismissed: (oneTimeMessageId: string) => boolean;
  setDismissed: (oneTimeMessageId: string) => boolean;
}

const shouldShowForMemberData = (
  supportedLanguages: string[],
  isNewAccount: boolean,
  isDismissed: boolean,
) => {
  if (isDismissed) {
    return false;
  }

  return isNewAccount && usesLanguages(supportedLanguages);
};

export const BoardsPageTemplatePickerUnconnected: FunctionComponent<
  BoardsPageTemplatePickerProps
> = ({ category = 'popular', isNewAccount, isDismissed, setDismissed }) => {
  const boardPickerTemplates: LocalizedTemplatesListHash =
    getBoardPickerTemplates(environment);

  const shouldRender = shouldShowForMemberData(
    Object.keys(boardPickerTemplates),
    isNewAccount(DATE_FOR_NEW_ACCOUNT),
    isDismissed(DISMISS_MESSAGE_ID),
  );
  const language = languageParts(currentLocale).language;

  const localizedBoardPickerTemplates = localizedTemplatesSelector(
    boardPickerTemplates,
    currentLocale,
    language,
  );

  const [selectedCategory, setCategory] = useState<Category>(category);

  useEffect(() => {
    if (!shouldRender) {
      return;
    }

    Analytics.sendViewedComponentEvent({
      componentType: 'section',
      componentName: 'templatePickerSection',
      source: 'memberBoardsHomeScreen',
    });
  }, [shouldRender]);

  const templateIds = useMemo(() => {
    // These are customized template IDs for "Most popular templates section" when the new users' combinations experiment is on.
    // Please read the following page for more information about the crossflow experiment:
    // https://hello.atlassian.net/wiki/spaces/PMXF/pages/3788113649/Trello+new+users+xflow+combinations+3+ads+on+Trello+XF+Experiment+Poster
    const customPopularCategoryEn = [
      '633352c430f89001edb7a933', // project management -> basic board
      '5e20e06c460b391727ce7a2b',
      '5de2721303a73434ebb2cc45', // simple project board -> daily task management
      '5a6a0d359ba9a3ff45dfe5a8',
    ];
    const customPopularCategoryFr = [
      '5e6005043fbdb55d9781821e',
      '5c4efa2bdcbd2f60958b4b8a',
    ];
    const customPopularCategoryPtBr = [
      '5e6005486e20e75989cf97e4',
      '5c4ef6244089b88c06bd9f2c',
      '5b6e7929895ea23dbd6329b5',
    ];
    if (environment === 'prod' && selectedCategory === 'popular') {
      if (language === 'en') {
        return customPopularCategoryEn;
      } else if (language === 'fr') {
        return customPopularCategoryFr;
      } else if (language === 'pt') {
        return customPopularCategoryPtBr;
      }
    }
    return localizedBoardPickerTemplates[selectedCategory];
  }, [language, localizedBoardPickerTemplates, selectedCategory]);

  if (!shouldRender) {
    return null;
  }

  const onDismiss = () => {
    Analytics.sendDismissedComponentEvent({
      componentType: 'section',
      componentName: 'templatePickerSection',
      source: 'memberBoardsHomeScreen',
    });

    try {
      return setDismissed(DISMISS_MESSAGE_ID);
    } catch (err) {
      sendErrorEvent(err, {
        tags: {
          ownershipArea: 'trello-web-eng',
          feature: 'Boards Page Template Picker',
        },
      });
    }
  };

  const onClickTemplate = (boardId: string) => {
    Analytics.sendUIEvent({
      action: 'clicked',
      actionSubject: 'sectionItem',
      actionSubjectId: 'templateTileSectionItem',
      source: 'templatePickerSection',
      containers: {
        board: { id: boardId },
      },
    });
  };

  const onChangeCategory = ({
    value: newSelectedCategory,
  }: {
    value: Category;
  }) => {
    Analytics.sendUIEvent({
      action: 'clicked',
      actionSubject: 'dropdownItem',
      actionSubjectId: 'templatePickerCategoryDropdownItem',
      source: 'templatePickerSection',
      attributes: {
        category: newSelectedCategory,
      },
    });

    setCategory(newSelectedCategory);
  };

  if (!templateIds?.length) {
    return null;
  }
  return (
    <TemplatePicker
      templateIds={templateIds}
      onDismiss={onDismiss}
      onTemplateClicked={onClickTemplate}
      selectedCategory={selectedCategory}
      onChangeCategory={onChangeCategory}
    />
  );
};

export const BoardsPageTemplatePicker: FunctionComponent<
  BoardsPageTemplatePickerProps
> = (props) => (
  <ComponentWrapper>
    <BoardsPageTemplatePickerUnconnected {...props} />
  </ComponentWrapper>
);
